import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import {
  X,
  Download,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileSpreadsheet,
  Image as ImageIcon,
  RotateCcw,
} from 'lucide-react'
import { showToastMessage } from './Toast'

export interface PreviewFile {
  id?: string
  name: string
  size?: string
  pageCount?: number
  type?: string
  url?: string
  src?: string
}

export interface DocumentPreviewModalProps {
  open: boolean
  file: PreviewFile | null
  onClose: () => void
  onDownload?: (file: PreviewFile) => void
}

function getFileExtension(filename: string): string {
  const parts = filename.split('.')
  if (parts.length > 1) {
    return parts[parts.length - 1].toLowerCase()
  }
  return 'pdf'
}

export function DocumentPreviewModal({
  open,
  file,
  onClose,
  onDownload,
}: DocumentPreviewModalProps) {
  const [zoom, setZoom] = useState<number>(100)
  const [currentPage, setCurrentPage] = useState<number>(1)

  useEffect(() => {
    if (open) {
      setZoom(100)
      setCurrentPage(1)
    }
  }, [open, file])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  if (!open || !file) return null

  const ext = getFileExtension(file.name)
  const sizeStr = file.size || '2.4 MB'
  const totalPages = file.pageCount || (ext === 'pdf' ? 3 : 1)

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(200, prev + 25))
  }

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(50, prev - 25))
  }

  const handleResetZoom = () => {
    setZoom(100)
  }

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }

  const handleDownloadFile = () => {
    if (onDownload) {
      onDownload(file)
    } else {
      showToastMessage(`Downloading "${file.name}"...`, 'success')
      if (file.url || file.src) {
        const a = document.createElement('a')
        a.href = file.url || file.src || ''
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    }
  }

  // File Badge Colors
  const getBadgeStyle = () => {
    if (ext === 'pdf') {
      return {
        bg: 'bg-rose-50 text-rose-600 border-rose-200',
        icon: <FileText className="w-4 h-4 text-rose-600 shrink-0" />,
        label: 'PDF',
      }
    }
    if (ext === 'doc' || ext === 'docx') {
      return {
        bg: 'bg-blue-50 text-blue-600 border-blue-200',
        icon: <FileText className="w-4 h-4 text-blue-600 shrink-0" />,
        label: 'DOCX',
      }
    }
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') {
      return {
        bg: 'bg-emerald-50 text-emerald-600 border-emerald-200',
        icon: <FileSpreadsheet className="w-4 h-4 text-emerald-600 shrink-0" />,
        label: 'EXCEL',
      }
    }
    return {
      bg: 'bg-purple-50 text-purple-600 border-purple-200',
      icon: <ImageIcon className="w-4 h-4 text-purple-600 shrink-0" />,
      label: ext.toUpperCase(),
    }
  }

  const badge = getBadgeStyle()

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-slate-900/70 backdrop-blur-xs font-sans animate-fadeIn"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Document Viewer - ${file.name}`}
        className="flex-1 flex flex-col w-full h-full bg-white overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* ── MODAL HEADER (TOP BAR) ── */}
        <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between z-20 shadow-xs shrink-0 min-w-0">
          {/* Left: File Badge + Name + Subtext (Page X of N • Size) */}
          <div className="flex items-center gap-3 min-w-0 flex-1 pr-4">
            <div className={`px-2 py-1 rounded-md border text-[11px] font-extrabold uppercase flex items-center gap-1.5 shrink-0 ${badge.bg}`}>
              {badge.icon}
              <span>{badge.label}</span>
            </div>

            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-base font-bold text-slate-900 truncate" title={file.name}>
                {file.name}
              </h3>
              <p className="text-xs text-slate-500 font-medium truncate mt-0.5">
                Page {currentPage} of {totalPages} • {sizeStr}
              </p>
            </div>
          </div>

          {/* Right: Zoom + Page Nav + Download + Close */}
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Zoom Controls */}
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 border border-slate-200">
              <button
                type="button"
                onClick={handleZoomOut}
                disabled={zoom <= 50}
                className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                title="Zoom Out (Ctrl -)"
              >
                <ZoomOut className="w-4 h-4" />
              </button>

              <span
                onClick={handleResetZoom}
                className="text-xs font-bold text-slate-700 w-12 text-center cursor-pointer hover:text-[#146971] select-none"
                title="Click to reset zoom (100%)"
              >
                {zoom}%
              </span>

              <button
                type="button"
                onClick={handleZoomIn}
                disabled={zoom >= 200}
                className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                title="Zoom In (Ctrl +)"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            <div className="w-px h-5 bg-slate-200 mx-1 hidden sm:block" />

            {/* Page Navigation Controls */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 border border-slate-200">
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={currentPage <= 1}
                  className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  title="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs font-bold text-slate-700 px-1 select-none whitespace-nowrap">
                  {currentPage} / {totalPages}
                </span>
                <button
                  type="button"
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className="p-1.5 text-slate-600 hover:text-slate-900 hover:bg-white rounded-md disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                  title="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="w-px h-5 bg-slate-200 mx-1" />

            {/* Download Button */}
            <button
              type="button"
              onClick={handleDownloadFile}
              className="p-2 text-slate-600 hover:text-[#146971] hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
              title="Download File"
            >
              <Download className="w-4.5 h-4.5" />
            </button>

            {/* Close Button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer ml-1"
              title="Close Preview (Esc)"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* ── MODAL BODY: CENTERED SCROLLABLE VIEWER ── */}
        <div className="flex-1 bg-slate-100 overflow-auto p-4 sm:p-8 flex items-start justify-center min-h-0 relative">
          <div
            className="transition-transform duration-200 ease-out origin-top my-auto py-4"
            style={{
              transform: `scale(${zoom / 100})`,
              width: `${Math.min(800, window.innerWidth - 64)}px`,
            }}
          >
            {/* White Paper Page Card */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden p-6 sm:p-10 space-y-6 text-slate-800 min-h-[900px] flex flex-col justify-between">
              {/* Page Header */}
              <div className="space-y-4 border-b border-slate-200 pb-5">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
                  <span className="uppercase tracking-wider">ACADEMIC EVALUATION SHEET</span>
                  <span>CONFIDENTIAL WORK</span>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">
                      {file.name.replace(/\.[^/.]+$/, '')}
                    </h2>
                    <p className="text-xs text-slate-500 font-medium mt-1">
                      Mathematics &bull; Exercise 4.2 Solutions &bull; Grade 8
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Page {currentPage} of {totalPages}
                    </span>
                  </div>
                </div>
              </div>

              {/* Page Body Content */}
              {file.url || file.src ? (
                <div className="flex-1 flex items-center justify-center p-2">
                  <img
                    src={file.url || file.src}
                    alt={file.name}
                    className="max-w-full max-h-[750px] object-contain rounded-lg shadow-2xs border border-slate-200"
                  />
                </div>
              ) : currentPage === 1 ? (
                <div className="flex-1 space-y-6 text-sm text-slate-700 font-sans py-2">
                  {/* Problem 1 */}
                  <div className="space-y-2 bg-slate-50/70 p-4 rounded-xl border border-slate-200/80">
                    <div className="font-bold text-slate-900 flex items-center justify-between">
                      <span>Problem 1: Solve the quadratic equation x&sup2; - 5x + 6 = 0</span>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Full Marks (5/5)
                      </span>
                    </div>
                    <div className="space-y-1 font-mono text-xs text-slate-800 bg-white p-3 rounded-lg border border-slate-200">
                      <div>x&sup2; - 5x + 6 = 0</div>
                      <div>(x - 2)(x - 3) = 0</div>
                      <div>x - 2 = 0 ==&gt; x = 2</div>
                      <div>x - 3 = 0 ==&gt; x = 3</div>
                      <div className="text-emerald-700 font-bold mt-1">Final Answer: x = 2, x = 3 &check;</div>
                    </div>
                  </div>

                  {/* Problem 2 */}
                  <div className="space-y-2 bg-slate-50/70 p-4 rounded-xl border border-slate-200/80">
                    <div className="font-bold text-slate-900 flex items-center justify-between">
                      <span>Problem 2: Find the value of k if 2x&sup2; + kx + 3 = 0 has equal roots</span>
                      <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                        Partial Credit (3/5)
                      </span>
                    </div>
                    <div className="space-y-1 font-mono text-xs text-slate-800 bg-white p-3 rounded-lg border border-slate-200">
                      <div>For equal roots: Discriminant &Delta; = b&sup2; - 4ac = 0</div>
                      <div>Here a = 2, b = k, c = 3</div>
                      <div>k&sup2; - 4(2)(3) = 0 ==&gt; k&sup2; - 24 = 0</div>
                      <div>k&sup2; = 24 ==&gt; k = &plusmn;&radic;24 = &plusmn;2&radic;6</div>
                      <div className="text-rose-600 font-semibold mt-1">
                        [Teacher Note: Remember to simplify radicals to simplest surd form]
                      </div>
                    </div>
                  </div>
                </div>
              ) : currentPage === 2 ? (
                <div className="flex-1 space-y-6 text-sm text-slate-700 font-sans py-2">
                  {/* Problem 3 */}
                  <div className="space-y-2 bg-slate-50/70 p-4 rounded-xl border border-slate-200/80">
                    <div className="font-bold text-slate-900 flex items-center justify-between">
                      <span>Problem 3: Word Problem &mdash; Area of Rectangular Field</span>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                        Full Marks (5/5)
                      </span>
                    </div>
                    <div className="space-y-1 font-mono text-xs text-slate-800 bg-white p-3 rounded-lg border border-slate-200">
                      <div>Let width = w meters. Then length = 2w + 1 meters.</div>
                      <div>Area = w &times; (2w + 1) = 528 m&sup2;</div>
                      <div>2w&sup2; + w - 528 = 0</div>
                      <div>Using quadratic formula: w = (-1 &plusmn; &radic;(1 - 4(2)(-528))) / 4</div>
                      <div>w = (-1 &plusmn; 65) / 4 ==&gt; w = 16 meters (length = 33 meters)</div>
                      <div className="text-emerald-700 font-bold mt-1">Final Answer: Length = 33m, Width = 16m &check;</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex-1 space-y-6 text-sm text-slate-700 font-sans py-2">
                  {/* Teacher Summary & Signature */}
                  <div className="border border-teal-200 bg-teal-50/40 p-5 rounded-xl space-y-3">
                    <h4 className="font-bold text-teal-900 text-base">Teacher Final Remarks &amp; Score Summary</h4>
                    <p className="text-xs text-slate-700 leading-relaxed">
                      Excellent submission overall. All work was clearly presented with proper algebraic steps. Please ensure units are included in final answers for word problems.
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-teal-200/80 text-xs font-bold text-teal-900">
                      <span>Total Score: 18 / 20 (90%)</span>
                      <span>Graded on: 15 Jun 2026</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Page Footer */}
              <div className="border-t border-slate-200 pt-4 flex items-center justify-between text-xs text-slate-400 font-medium">
                <span>School Admin System &bull; Homework Verification Module</span>
                <span>Page {currentPage} of {totalPages}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return typeof document === 'undefined'
    ? modalContent
    : createPortal(modalContent, document.body)
}
